import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useStoreState, useStoreActions } from 'easy-peasy';
import './Dashboard.scss';

const POOLS_FETCHED_KEY = 'poolsFetchedInSession';

export default function PoolSelector({ onPoolSelect, selectedPoolId, selectedShaftId }) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  
  const { pools, loading } = useStoreState((state) => state.pools);
  const { getPools } = useStoreActions((actions) => actions.pools);

  // Debug logging
  console.log('PoolSelector: pools state:', pools);
  console.log('PoolSelector: loading state:', loading);
  console.log('PoolSelector: pools type:', typeof pools);
  console.log('PoolSelector: pools is array:', Array.isArray(pools));
  console.log('PoolSelector: selectedPoolId prop:', selectedPoolId);
  console.log('PoolSelector: selectedShaftId prop:', selectedShaftId);

  useEffect(() => {
    const poolsFetched = sessionStorage.getItem(POOLS_FETCHED_KEY);
    
    // Only fetch if not already fetched in this session and store is empty
    if (!poolsFetched && pools.length === 0 && !loading) {
      console.log('PoolSelector: Fetching pools (first time in session)');
      getPools()
        .then(() => {
          sessionStorage.setItem(POOLS_FETCHED_KEY, 'true');
          console.log('PoolSelector: Pools fetched and marked in sessionStorage');
        })
        .catch(error => {
          console.error('PoolSelector: Error fetching pools:', error);
        });
    } else {
      console.log('PoolSelector: Pools already fetched or loaded, skipping fetch. poolsFetched:', poolsFetched, 'pools.length:', pools.length);
    }
  }, []); // Empty dependency array - only run on mount

  useEffect(() => {
    // Set pool from prop if provided, otherwise don't set any default
    if (pools && pools.length > 0) {
      if (selectedPoolId) {
        // Find the pool by ID from props
        const pool = pools.find(p => p.id === selectedPoolId);
        if (pool && pool.id !== selectedPool?.id) {
          console.log('PoolSelector: Setting pool from prop:', pool);
          setSelectedPool(pool);
        }
      }
      // Removed automatic selection of first pool to allow "Kaikki allakset" as default
    }
  }, [pools, selectedPoolId, selectedPool]);

  const toggle = () => setDropdownOpen(prev => !prev);

  const handlePoolSelect = (pool) => {
    console.log('PoolSelector: Pool selected:', pool);
    setSelectedPool(pool);
    setDropdownOpen(false);
    
    // Call the callback if provided
    if (onPoolSelect) {
      onPoolSelect(pool);
    }
  };

  // Ensure pools is an array before mapping
  const poolsArray = Array.isArray(pools) ? pools : [];
  
  // Filter pools by selected shaft if provided
  const filteredPools = selectedShaftId
    ? poolsArray.filter(pool => {
        const poolShaftId = typeof pool.shaft === 'object' ? pool.shaft?.id : pool.shaft;
        return poolShaftId === selectedShaftId;
      })
    : poolsArray;
  
  console.log('PoolSelector: poolsArray:', poolsArray);
  console.log('PoolSelector: filteredPools:', filteredPools);

  return (
    <div className="pool-selector d-flex align-items-center">
      <i className="fas fa-map-marker-alt mr-2" />
      <Dropdown isOpen={dropdownOpen} toggle={toggle} className="pool-dropdown">
        <DropdownToggle caret color="link" className="p-0 text-dark">
          {selectedPool ? selectedPool.name : t('pool_all_pools')}
        </DropdownToggle>
        <DropdownMenu container="body" className="pump-status-filter-menu">
          <DropdownItem onClick={() => handlePoolSelect(null)}>
            {t('pool_all_pools')}
          </DropdownItem>
          {filteredPools.map(pool => (
            <DropdownItem key={pool.id} onClick={() => handlePoolSelect(pool)}>
              {pool.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
} 